// Generat automat din README.md - Auto-save System for Forms
import React, { useState, useEffect, useCallback, useRef } from 'react';

export const useAutoSave = (
  formData,
  saveFunction,
  options = {}
) => {
  const {
    delay = 2000, // 2 seconds default
    enabled = true,
    onSaveSuccess,
    onSaveError,
    storageKey = null // For localStorage backup
  } = options;

  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const timeoutRef = useRef(null);
  const lastDataRef = useRef(null);

  // Save to localStorage as backup
  const saveToStorage = useCallback((data) => {
    if (storageKey && typeof window !== 'undefined') {
      try {
        localStorage.setItem(storageKey, JSON.stringify({
          data,
          timestamp: Date.now()
        }));
      } catch (error) {
        console.warn('Failed to save to localStorage:', error);
      }
    }
  }, [storageKey]);

  // Load from localStorage
  const loadFromStorage = useCallback(() => {
    if (storageKey && typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem(storageKey);
        if (saved) {
          const parsed = JSON.parse(saved);
          return parsed.data;
        }
      } catch (error) {
        console.warn('Failed to load from localStorage:', error);
      }
    }
    return null;
  }, [storageKey]);

  // Clear localStorage backup
  const clearStorage = useCallback(() => {
    if (storageKey && typeof window !== 'undefined') {
      localStorage.removeItem(storageKey);
    }
  }, [storageKey]);

  // Auto-save logic
  useEffect(() => {
    if (!enabled) return;

    // Check if data has actually changed
    const currentDataString = JSON.stringify(formData);
    const lastDataString = JSON.stringify(lastDataRef.current);

    if (currentDataString === lastDataString) {
      return; // No changes
    }

    setHasUnsavedChanges(true);
    saveToStorage(formData);

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout for auto-save
    timeoutRef.current = setTimeout(async () => {
      if (saveFunction && typeof saveFunction === 'function') {
        setIsSaving(true);
        try {
          await saveFunction(formData);
          setLastSaved(new Date());
          setHasUnsavedChanges(false);
          onSaveSuccess?.(formData);
          // Clear localStorage backup after successful save
          clearStorage();
        } catch (error) {
          console.error('Auto-save failed:', error);
          onSaveError?.(error);
        } finally {
          setIsSaving(false);
        }
      }
    }, delay);

    lastDataRef.current = formData;

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [formData, saveFunction, delay, enabled, onSaveSuccess, onSaveError, saveToStorage, clearStorage]);

  // Manual save function
  const saveNow = useCallback(async () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (saveFunction && typeof saveFunction === 'function') {
      setIsSaving(true);
      try {
        await saveFunction(formData);
        setLastSaved(new Date());
        setHasUnsavedChanges(false);
        onSaveSuccess?.(formData);
        clearStorage();
        return { success: true };
      } catch (error) {
        console.error('Manual save failed:', error);
        onSaveError?.(error);
        return { success: false, error };
      } finally {
        setIsSaving(false);
      }
    }
  }, [formData, saveFunction, onSaveSuccess, onSaveError, clearStorage]);

  // Check for unsaved changes before page unload
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = 'Ai modificări nesalvate. Ești sigur că vrei să părăsești pagina?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  return {
    isSaving,
    lastSaved,
    hasUnsavedChanges,
    saveNow,
    loadFromStorage,
    clearStorage
  };
};

// Auto-save Status Indicator Component
export const AutoSaveIndicator = ({ 
  isSaving, 
  lastSaved, 
  hasUnsavedChanges,
  className = "" 
}) => {
  const formatTime = (date) => {
    if (!date) return '';
    const now = new Date();
    const diff = Math.floor((now - date) / 1000); // seconds

    if (diff < 60) return 'acum câteva secunde';
    if (diff < 3600) return `acum ${Math.floor(diff / 60)} minute`;
    return date.toLocaleTimeString('ro-RO');
  };

  return (
    <div className={`flex items-center space-x-2 text-sm ${className}`}>
      {isSaving ? (
        <>
          <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-blue-600 dark:text-blue-400">Se salvează...</span>
        </>
      ) : hasUnsavedChanges ? (
        <>
          <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
          <span className="text-yellow-600 dark:text-yellow-400">Modificări nesalvate</span>
        </>
      ) : lastSaved ? (
        <>
          <div className="w-3 h-3 bg-green-500 rounded-full"></div>
          <span className="text-green-600 dark:text-green-400">
            Salvat {formatTime(lastSaved)}
          </span>
        </>
      ) : (
        <>
          <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
          <span className="text-gray-500 dark:text-gray-400">Nesalvat</span>
        </>
      )}
    </div>
  );
};

// Enhanced Form Component with Auto-save
export const AutoSaveForm = ({ 
  children, 
  onSubmit, 
  autoSaveOptions = {},
  className = "" 
}) => {
  const [formData, setFormData] = useState({});
  
  const {
    isSaving,
    lastSaved,
    hasUnsavedChanges,
    saveNow
  } = useAutoSave(
    formData,
    autoSaveOptions.saveFunction,
    autoSaveOptions
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    await saveNow();
    onSubmit?.(formData);
  };

  return (
    <form onSubmit={handleSubmit} className={className}>
      <div className="space-y-4">
        {/* Auto-save Status */}
        <div className="flex justify-between items-center">
          <AutoSaveIndicator
            isSaving={isSaving}
            lastSaved={lastSaved}
            hasUnsavedChanges={hasUnsavedChanges}
          />
          
          {hasUnsavedChanges && (
            <button
              type="button"
              onClick={saveNow}
              disabled={isSaving}
              className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              Salvează Acum
            </button>
          )}
        </div>

        {/* Form Fields */}
        <div className="form-context" data-form-data={JSON.stringify(formData)}>
          {typeof children === 'function' 
            ? children({ formData, setFormData, isSaving }) 
            : children
          }
        </div>
      </div>
    </form>
  );
};

// Form Recovery Modal
export const FormRecoveryModal = ({ 
  isOpen, 
  onRecover, 
  onDiscard, 
  recoveredData,
  onClose 
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Date Recuperate
        </h3>
        
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          Am găsit date nesalvate din sesiunea anterioară. Vrei să le recuperezi?
        </p>

        {recoveredData && (
          <div className="bg-gray-50 dark:bg-gray-700 rounded-md p-3 mb-4 max-h-32 overflow-y-auto">
            <pre className="text-xs text-gray-700 dark:text-gray-300">
              {JSON.stringify(recoveredData, null, 2)}
            </pre>
          </div>
        )}

        <div className="flex space-x-3">
          <button
            onClick={onRecover}
            className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700"
          >
            Recuperează
          </button>
          <button
            onClick={onDiscard}
            className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500"
          >
            Renunță
          </button>
        </div>
      </div>
    </div>
  );
};